import { type OnChangeFn, type SortingState, type ColumnDef, type RowData } from "@tanstack/react-table";
import { type ReactNode } from "react";
import { DndContextProvider } from "./DndContextProvider";
import { TableImplementation } from "./TableImplementation";
import { useTableColumnOrder } from "@/components/DataTable/useTableColumnOrder";
import { type PaginationProperties } from "@/components/Pagination";

export interface TableProps<TData extends RowData> {
  data: Array<TData>;
  pagination: PaginationProperties;
  tableId: string;
  columns: Array<ColumnDef<TData, any>>;
  loading: boolean;
  errorMessage?: string;
  sortConfig?: {
    sorting: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
  };
  onRowClick?: (row: TData) => void;
  filterButton?: {
    onClick: () => void;
    activeCount?: number;
    label?: string;
  };
  searchFilter?: ReactNode;
}

export function DataTable<TData extends RowData>({
  data,
  pagination,
  tableId,
  sortConfig,
  columns,
  loading,
  errorMessage,
  onRowClick,
  filterButton,
  searchFilter,
}: TableProps<TData>) {
  const { columnOrder, handleDragEnd, onColumnOrderChange } = useTableColumnOrder(tableId, columns);

  return (
    <DndContextProvider onDragEnd={handleDragEnd}>
      <TableImplementation
        columns={columns}
        loading={loading}
        errorMessage={errorMessage}
        data={data}
        pagination={pagination}
        tableId={tableId}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        sortConfig={sortConfig}
        onRowClick={onRowClick}
        filterButton={filterButton}
        searchFilter={searchFilter}
      />
    </DndContextProvider>
  );
}
